// src/pages/AccessibilityPage.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { submitAccessibilityFeedback } from '../lib/db';
import toast from 'react-hot-toast';

const TIPS = [
  ["Have a mobile number people can text, not just a landline to call.", "Deaf and hard-of-hearing people, anyone anxious about phone calls, people with speech difficulties"],
  ["Offer booking or questions by text, email, or an online form — not phone-only.", "Deaf and hard-of-hearing customers, busy people, anyone who processes written information better"],
  ["Use plain, large-print signage with strong colour contrast.", "People with low vision, older adults, people with dyslexia"],
  ["Keep at least one entrance step-free, clearly signed.", "Wheelchair users, parents with buggies, older adults, anyone with a temporary injury"],
  ["Face the person and speak clearly — don't cover your mouth.", "People who lip-read, hard-of-hearing customers"],
  ["Make menus, price lists, and forms available in large print on request.", "People with low vision, older adults"],
  ["Repeat back or write down key details instead of assuming a call was understood.", "Deaf and hard-of-hearing customers, non-native English speakers"],
  ["Keep an accessible toilet clearly signed, unlocked, and unobstructed.", "Wheelchair users, people with hidden disabilities, older adults, parents"],
  ["Welcome a companion or support person without question or extra charge.", "People with disabilities, older adults who need assistance"],
  ["When in doubt, just ask \"how can I help?\" and listen to the answer.", "Everyone — costs nothing, avoids wrong assumptions"],
];

const WHAT_GATHER_DOES = [
  ["👁️", "Works with screen readers", "Real headings, labelled buttons and forms, and announcements when content changes — not just words on a page."],
  ["🔠", "Adjustable text size", "Genuinely scales the whole app, not just a few headings."],
  ["🎨", "High contrast display", "A real toggle that changes how the app looks, for anyone who needs stronger contrast to read comfortably."],
  ["🔓", "No login to browse", "Nobody should need an account just to see what's on in their own town."],
  ["🌍", "Multiple languages", "English, Irish, Polish, and Ukrainian — because a community noticeboard should speak to the whole community."],
];

const AccessibilityPage = () => {
  const navigate = useNavigate();
  const [feedback, setFeedback] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    if (!feedback.trim()) { toast.error('Add a note first'); return; }
    setSubmitting(true);
    try {
      await submitAccessibilityFeedback(feedback);
      setSubmitted(true);
      setFeedback('');
      toast.success('Thank you — sent anonymously');
    } catch (err) {
      console.error(err);
      toast.error('Could not send — please try again');
    }
    setSubmitting(false);
  };

  return (
    <div style={s.page}>
      <button onClick={() => navigate(-1)} style={s.back}>
        <div style={s.backCircle}>←</div>
        <span style={s.backLbl}>Back</span>
      </button>

      <div style={s.logo}>♿</div>
      <h1 style={s.h1}>Accessibility at Gather</h1>
      <p style={s.lead}>
        Information about your own town shouldn't have barriers around it — and that includes barriers around who can actually get to it.
      </p>

      <div style={s.section}>
        <div style={s.sectionTitle}>Why this matters to us</div>
        <p style={s.body}>
          Rural Irish towns have older populations, and older adults are more likely to be Deaf, hard of hearing, or living with low vision — exactly the people a community noticeboard exists to serve. Add in people with English as a second language, and "just make an app" isn't enough on its own.
        </p>
        <p style={s.body}>
          One thing we hear often: Deaf and hard-of-hearing people are frequently locked out of ordinary daily life by services that only take phone calls — booking a class, asking a question, sorting out a query — forcing them to rely on a family member to ring on their behalf. That's not independence, and it's avoidable. A business that also offers texting, email, or an online form isn't doing something exotic — it's removing a barrier that costs almost nothing to fix.
        </p>
        <p style={s.body}>
          We think about accessibility the same way we think about the rest of Gather: free, open, and built for everyone in the town, not just the people technology usually gets built for first.
        </p>
      </div>

      <div style={s.section}>
        <div style={s.sectionTitle}>What Gather does today</div>
        <div style={s.stepList}>
          {WHAT_GATHER_DOES.map(([emoji, title, desc]) => (
            <div key={title} style={s.featureRow}>
              <span style={s.stepEmoji}>{emoji}</span>
              <div>
                <div style={s.featureTitle}>{title}</div>
                <div style={s.featureDesc}>{desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={s.section}>
        <div style={s.sectionTitle}>Tips for local businesses</div>
        <p style={s.body}>
          Small, low-cost changes make a real difference to who can actually use your business. Ten places to start:
        </p>
        <div style={s.tipList}>
          {TIPS.map(([tip, who], i) => (
            <div key={i} style={s.tipRow}>
              <div style={s.tipNum}>{i + 1}</div>
              <div>
                <div style={s.tipText}>{tip}</div>
                <div style={s.tipWho}>({who})</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={s.section}>
        <div style={s.sectionTitle}>Got an idea?</div>
        <p style={s.body}>
          If you've got a suggestion for how Gather — or businesses in general — could be more accessible, we'd genuinely like to hear it. This goes straight to us, sent anonymously — no account, no name attached.
        </p>
        {submitted ? (
          <div style={s.thanksBox}>Thank you — that's been sent through anonymously. 🙏</div>
        ) : (
          <>
            <textarea
              value={feedback}
              onChange={e => setFeedback(e.target.value)}
              placeholder="Share an idea..."
              maxLength={1000}
              rows={4}
              aria-label="Accessibility suggestion"
              style={s.textarea}
            />
            <button onClick={handleSubmit} disabled={submitting} style={{ ...s.submitBtn, opacity: submitting ? 0.6 : 1 }}>
              {submitting ? 'Sending...' : 'Send anonymously'}
            </button>
          </>
        )}
      </div>

      <div style={s.card}>
        <div style={s.cardText}>Want a hand thinking through accessibility for your business? We're happy to talk.</div>
        <button onClick={() => navigate('/contact')} style={s.contactBtn}>Get in touch</button>
      </div>
    </div>
  );
};

const s = {
  page: { background: '#fff', minHeight: '100vh', padding: '52px 24px 60px', maxWidth: 430, margin: '0 auto' },
  back: { display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', marginBottom: 24 },
  backCircle: { width: 34, height: 34, background: '#F8FBF8', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, color: '#2C4A2E' },
  backLbl: { fontSize: 13, color: '#4A6A4A', fontWeight: 600 },
  logo: { fontSize: 40, marginBottom: 12 },
  h1: { fontSize: 28, fontWeight: 800, color: '#1A2E1A', marginBottom: 12, letterSpacing: '-0.3px' },
  lead: { fontSize: 16, color: '#2C4A2E', lineHeight: 1.6, marginBottom: 32, fontWeight: 500 },
  section: { marginBottom: 28 },
  sectionTitle: { fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px', color: '#1A7A4A', marginBottom: 10 },
  body: { fontSize: 15, color: '#3A5A3A', lineHeight: 1.7, margin: '0 0 12px 0' },
  stepList: { display: 'flex', flexDirection: 'column', gap: 12 },
  stepEmoji: { fontSize: 22, flexShrink: 0, width: 28, textAlign: 'center' },
  featureRow: { display: 'flex', alignItems: 'flex-start', gap: 12, background: '#F8FBF8', borderRadius: 12, padding: '12px 14px' },
  featureTitle: { fontSize: 14, color: '#1A2E1A', fontWeight: 700, marginBottom: 2 },
  featureDesc: { fontSize: 13, color: '#4A6A4A', fontWeight: 500, lineHeight: 1.5 },
  tipList: { display: 'flex', flexDirection: 'column', gap: 10 },
  tipRow: { display: 'flex', alignItems: 'flex-start', gap: 12, background: '#F8FBF8', borderRadius: 12, padding: '12px 14px' },
  tipNum: { width: 24, height: 24, borderRadius: '50%', background: '#1A7A4A', color: 'white', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 },
  tipText: { fontSize: 14.5, color: '#1A2E1A', fontWeight: 600, lineHeight: 1.5, marginBottom: 3 },
  tipWho: { fontSize: 12.5, color: '#4A6A4A', fontWeight: 500, fontStyle: 'italic' },
  textarea: { width: '100%', border: '1.5px solid #D8E4D8', borderRadius: 12, padding: 12, fontSize: 14.5, fontFamily: 'Nunito, sans-serif', color: '#1A2E1A', resize: 'vertical', boxSizing: 'border-box', marginBottom: 10 },
  submitBtn: { width: '100%', background: '#1A7A4A', color: 'white', border: 'none', borderRadius: 12, padding: '13px 20px', fontSize: 14.5, fontWeight: 700, fontFamily: 'Nunito, sans-serif', cursor: 'pointer' },
  thanksBox: { background: '#EBF2EB', borderRadius: 12, padding: '14px 16px', fontSize: 14, color: '#2C4A2E', fontWeight: 600, textAlign: 'center' },
  card: { background: '#EBF2EB', borderRadius: 16, padding: '20px', textAlign: 'center', marginTop: 12 },
  cardText: { fontSize: 14, color: '#2C4A2E', fontWeight: 500, marginBottom: 14, lineHeight: 1.5 },
  contactBtn: { background: '#1A7A4A', color: 'white', border: 'none', borderRadius: 12, padding: '12px 24px', fontSize: 14, fontWeight: 700, fontFamily: 'Nunito, sans-serif', cursor: 'pointer' },
};

export default AccessibilityPage;
